<template>
<div class="ew-page">
  <navbar-dashboard name="Usuarios"></navbar-dashboard>

  <div class="ew-page__body">
    <div class="ew-toolbar ew-toolbar--end">
      <button class="ew-btn-primary" @click="showModal('')">Nuevo usuario</button>
    </div>

    <b-modal ref="addUser" hide-footer v-bind:title="title" modal-class="ew-modal">
      <form @submit.prevent="registerUser" class="ew-form">
        <div class="ew-form__field">
          <label class="ew-label" for="u-first">Nombre *</label>
          <input id="u-first" type="text" class="ew-input ew-input--block" placeholder="Nombre" v-model="firstName" required>
        </div>
        <div class="ew-form__field">
          <label class="ew-label" for="u-last">Apellidos *</label>
          <input id="u-last" type="text" class="ew-input ew-input--block" placeholder="Apellidos" v-model="lastName" required>
        </div>
        <div class="ew-form__field">
          <label class="ew-label" for="u-phone">Teléfono *</label>
          <input id="u-phone" type="text" class="ew-input ew-input--block" placeholder="(442) 123 12 45" v-model="phone" required>
        </div>
        <div class="ew-form__field">
          <label class="ew-label" for="u-email">Correo *</label>
          <input id="u-email" type="email" class="ew-input ew-input--block" placeholder="correo@ejemplo.com" v-model="email" required>
        </div>
        <div class="ew-form__field">
          <label class="ew-label" for="u-password">Contraseña *</label>
          <input id="u-password" type="password" class="ew-input ew-input--block" placeholder="Mínimo 8 caracteres" v-model="password" required>
        </div>
        <div class="ew-modal__actions">
          <button type="button" class="ew-btn-outline" @click="hideModal">Cancelar</button>
          <button class="ew-btn-primary" type="submit">Crear usuario</button>
        </div>
      </form>
    </b-modal>

    <b-modal ref="creditModal" hide-footer title="Abonar saldo" modal-class="ew-modal">
      <form @submit.prevent="confirmCredit" class="ew-form" v-if="creditingUser">
        <p class="ew-credit-target">
          Saldo actual de <strong>{{ creditingUser.firstName }} {{ creditingUser.lastName }}</strong>:
          {{ formatMXN(creditingUser.total) }}
        </p>

        <p class="alert alert-danger ew-alert" role="alert" v-if="creditError">{{ creditError }}</p>

        <template v-if="!creditConfirming">
          <div class="ew-amount-field">
            <span class="ew-amount-field__prefix">$</span>
            <input
              type="number"
              min="0.01"
              step="0.01"
              class="ew-amount-field__input"
              placeholder="0.00"
              v-model="creditAmount"
              required
            >
            <span class="ew-amount-field__suffix">MXN</span>
          </div>
          <p class="ew-amount-preview">{{ formatMXN(creditAmount) }} MXN</p>
          <p class="alert alert-warning ew-alert" role="alert" v-if="creditAmount > creditWarningThreshold">
            Es un monto alto ({{ formatMXN(creditAmount) }}). Revisa que esté correcto antes de continuar.
          </p>

          <div class="ew-quick-amounts">
            <button
              type="button"
              class="ew-chip"
              v-for="q in creditQuickAmounts"
              :key="q"
              @click="creditAmount = q"
            >{{ formatMXN(q) }}</button>
          </div>

          <div class="ew-modal__actions">
            <button type="button" class="ew-btn-outline" @click="hideCreditModal">Cancelar</button>
            <button class="ew-btn-primary" type="submit" :disabled="!creditAmount || creditAmount <= 0">Abonar saldo</button>
          </div>
        </template>

        <template v-else>
          <p class="ew-credit-target">
            ¿Confirmas abonar <strong>{{ formatMXN(creditAmount) }}</strong> al saldo de
            <strong>{{ creditingUser.firstName }} {{ creditingUser.lastName }}</strong>?
          </p>
          <div class="ew-modal__actions">
            <button type="button" class="ew-btn-outline" :disabled="isCrediting" @click="creditConfirming = false">Cambiar monto</button>
            <button class="ew-btn-primary" type="submit" :disabled="isCrediting">
              {{ isCrediting ? 'Abonando…' : 'Confirmar abono' }}
            </button>
          </div>
        </template>
      </form>
    </b-modal>

    <section class="ew-card">
      <div class="ew-filters">
        <span class="ew-filters__label"><ph-icon name="magnifying-glass" />Buscar usuario</span>
        <div class="ew-search">
          <input
            type="text"
            class="ew-input ew-search__input"
            placeholder="Nombre, apellido o correo..."
            v-model="searchTerm"
            @input="onSearchInput"
            @keyup.enter="runSearch"
            @keydown.esc="showSuggestions = false"
            @focus="showSuggestions = searchTerm.length > 0"
            @blur="showSuggestions = false"
          >
          <ul class="ew-search__suggestions" v-if="showSuggestions && suggestions.length > 0">
            <li
              v-for="u in suggestions"
              :key="u._id"
              class="ew-search__suggestion"
              @mousedown.prevent="selectSuggestion(u)"
            >
              <span class="ew-search__suggestion-name">{{ u.firstName }} {{ u.lastName }}</span>
              <span class="ew-search__suggestion-email">{{ u.email }}</span>
            </li>
          </ul>
        </div>
      </div>

      <div class="table-responsive">
        <div class="ew-loading" v-if="dataLoading">
          <span class="ew-spinner"></span>
          <p class="ew-loading-text">Cargando usuarios…</p>
        </div>
        <table class="ew-table" v-else-if="users.length > 0">
          <thead>
            <tr>
              <th scope="col">Nombre</th>
              <th scope="col">Apellidos</th>
              <th scope="col">Correo</th>
              <th scope="col">Teléfono</th>
              <th scope="col">Saldo</th>
              <th scope="col">Estatus</th>
              <th scope="col">Acciones</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(u, i) in users" v-bind:key="i">
              <td>{{u.firstName}}</td>
              <td>{{u.lastName}}</td>
              <td>{{u.email}}</td>
              <td>{{u.phone}}</td>
              <td>{{ formatMXN(u.total) }}</td>
              <td>
                <span class="ew-status" :class="{ 'is-active': u.active }">{{ u.active ? 'Activo': 'Bloqueado' }}</span>
              </td>
              <td class="ew-actions-cell">
                <label class="ew-toggle" :class="{ 'is-on': u.active }" :title="u.active ? 'Desactivar usuario' : 'Activar usuario'">
                  <input type="checkbox" :checked="u.active" @change="u.active ? desactivarUsuario(u._id) : activarUsuario(u._id)">
                  <span class="ew-toggle__track"><span class="ew-toggle__thumb"></span></span>
                </label>
                <button class="ew-icon-btn" @click="openCreditModal(u)" aria-label="Abonar saldo" title="Abonar saldo">
                  <ph-icon name="wallet" />
                </button>
              </td>
            </tr>
          </tbody>
        </table>
        <div class="ew-empty" v-else-if="searchTerm">
          <ph-icon name="magnifying-glass" />
          <p class="ew-empty__title">Sin resultados</p>
          <p class="ew-empty__text">Ningún usuario coincide con "{{ searchTerm }}".</p>
        </div>
        <div class="ew-empty" v-else>
          <ph-icon name="users" />
          <p class="ew-empty__title">Aún no hay usuarios</p>
          <p class="ew-empty__text">Crea el primer usuario adicional de tu cuenta.</p>
        </div>
      </div>

      <div class="ew-pagination" v-if="totalPages > 1">
        <button class="ew-pagination__btn" :disabled="page <= 1" @click="goToPage(page - 1)">Anterior</button>
        <button
          class="ew-pagination__btn"
          @click="goToPage(p)"
          v-for="p in availablePages"
          :key="p"
          :class="{ 'is-active': page === p }"
        >{{ p }}</button>
        <button class="ew-pagination__btn" :disabled="page >= totalPages" @click="goToPage(page + 1)">Siguiente</button>
      </div>
    </section>
  </div>
</div>
</template>

<script>
import Navbar from './Header/Navbar'
import { mapGetters, mapActions, mapMutations } from 'vuex'
import api from '../commons/api'
import axios from 'axios'
import moment from 'moment'
import commons from '../commons'
import VueConfirmDialog from 'vue-confirm-dialog'
import Vue from 'vue'
import PhIcon from './icons/PhIcon'
import { formatMXN } from '../commons/currency'

Vue.use(VueConfirmDialog)
Vue.component('vue-confirm-dialog', VueConfirmDialog.default)

export default {
  name: 'Users',
  data () {
    return {
      title: 'Nuevo usuario',
      firstName: '',
      lastName: '',
      phone:'',
      email: '',
      password: '',
      user_id: '',
      users: [],
      page: 1,
      limit: 10,
      totalCount: 0,
      searchTerm: '',
      suggestions: [],
      showSuggestions: false,
      searchTimer: null,
      results: [],
      scroll:0,
      isLoading: false,
      dataLoading: true,
      arrowCounter: -1,
      creditingUser: null,
      creditAmount: 0,
      creditQuickAmounts: [500, 1000, 2000, 5000],
      creditWarningThreshold: 20000,
      creditConfirming: false,
      creditError: '',
      isCrediting: false,
    }
  },
  created () {
    this.user_id = localStorage.getItem('ew-user-id')
    this.getUsers()
    this.setItemActive('users')
  },
  mounted(){

  },
  beforeDestroy(){

  },
  methods: {
   ...mapMutations(['setItemActive']),

   formatDateTime(value) {
     if (value) {
       return moment(String(value)).format('DD/MM/YYYY HH:mm:ss')
     }
   },
   getUsers () {
     // userController.index devuelve { users, totalCount } (paginado del
     // lado del servidor, límite de 10 por página si no se manda `limit`) —
     // no un arreglo plano; por eso la tabla nunca mostraba nada. La
     // respuesta no trae links previous/next ni `pages` (a diferencia de
     // /shipments/all), así que la paginación se arma aquí con page/limit
     // + totalCount. `filter` hace match por nombre/apellido/correo del
     // lado del servidor (no solo en lo ya cargado), así funciona con
     // cualquier cantidad de usuarios, no solo la página visible.
     axios
       .get(`${commons.apiURL}/api/v1/users`, {
         params: {
           page: this.page,
           limit: this.limit,
           filter: this.searchTerm.trim()
         }
       })
       .then(response => {
           this.users = response.data.users
           this.totalCount = response.data.totalCount
           this.suggestions = this.searchTerm.trim() ? this.users.slice(0, 6) : []
       })
       .catch(error => {
         console.error(error)
       })
       .finally(() => {
         this.dataLoading = false
       })
   },
   goToPage (page) {
     if (page < 1 || page > this.totalPages) return
     this.page = page
     this.getUsers()
   },
   onSearchInput () {
     this.showSuggestions = this.searchTerm.length > 0
     clearTimeout(this.searchTimer)
     this.searchTimer = setTimeout(() => {
       this.page = 1
       this.getUsers()
     }, 300)
   },
   runSearch () {
     clearTimeout(this.searchTimer)
     this.showSuggestions = false
     this.page = 1
     this.getUsers()
   },
   selectSuggestion (u) {
     // Buscar por nombre+apellido no funcionaría: el backend hace match por
     // campo (firstName O lastName O email), ninguno contiene la cadena
     // concatenada completa. El correo es único (unique:true en el modelo),
     // así que siempre encuentra exactamente a esa persona.
     this.searchTerm = u.email
     this.showSuggestions = false
     this.page = 1
     this.getUsers()
   },
   activarUsuario(id)
   {
       axios
         .put(`${commons.apiURL}/api/v1/users/${id}`,
         {
           active: true
         })
         .then(response => {
             if(response.status == 200)
             {
                this.getUsers()
             }
         })
         .catch(error => {
           console.error(error)
         })
   },
   desactivarUsuario(id)
   {
       axios
         .put(`${commons.apiURL}/api/v1/users/${id}`,
         {
           active: false
         })
         .then(response => {
             if(response.status == 200)
             {
                this.getUsers()
             }
         })
         .catch(error => {
           console.error(error)
         })
   },
   registerUser()
   {
     axios
       .post(`${commons.apiURL}/api/v1/users/createFromDashboard`, {
         firstName: this.firstName,
         lastName: this.lastName,
         email: this.email,
         phone: this.phone,
         password: this.password,
         rol: 'user'
       })
       .then(response => {
           if(response.status == 200)
           {
              this.hideModal()
              this.getUsers()
           }
       })
       .catch(error => {
         console.error(error)
       })
   },
   showModal () {
     this.firstName = ''
     this.lastName = ''
     this.phone = ''
     this.email = ''
     this.password = ''
     this.$refs.addUser.show()
   },
   hideModal () {
     this.$refs.addUser.hide()
   },
   formatMXN,
   openCreditModal (u) {
     this.creditingUser = u
     this.creditAmount = 0
     this.creditError = ''
     this.creditConfirming = false
     this.$refs.creditModal.show()
   },
   hideCreditModal () {
     this.$refs.creditModal.hide()
   },
   confirmCredit () {
     // Fase 3, punto 3 del plan de monedero: antes un solo click abonaba
     // dinero real sin ningún paso intermedio — un typo de un dígito de más
     // no tenía ningún freno. Este primer click solo pasa al paso de
     // confirmación (ver template); el que de verdad cobra es el segundo.
     if (!this.creditConfirming) {
       this.creditConfirming = true
       return
     }
     this.isCrediting = true
     this.creditError = ''
     // Reutiliza el endpoint que ya usa la recarga de saldo del propio
     // usuario (NewPrepaid.vue), pero con status:true de una vez — un
     // abono manual desde el panel ya está confirmado por el admin, no
     // pasa por PayPal como el flujo de autoservicio.
     axios
       .post(`${commons.apiURL}/api/v1/prepaid`, {
         user: this.creditingUser._id,
         amount: this.creditAmount,
         status: true,
         type: 'Abono manual (admin)'
       })
       .then(() => {
         this.hideCreditModal()
         this.getUsers()
       })
       .catch(error => {
         console.error(error)
         this.creditConfirming = false
         this.creditError = 'No se pudo abonar el saldo. Intenta de nuevo.'
       })
       .finally(() => {
         this.isCrediting = false
       })
   },

  },
  computed: {
   ...mapGetters(['itemActive']),
   totalPages () {
     return Math.ceil(this.totalCount / this.limit) || 1
   },
   availablePages () {
     const result = []
     const initial = this.page > 1 ? this.page - 1 : 1
     const final = this.page < this.totalPages ? this.page + 1 : this.totalPages
     for (let i = initial; i <= final; i++) {
       result.push(i)
     }
     return result
   }
  },
  components: {
    'navbar-dashboard': Navbar,
    PhIcon
  },
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped lang="scss">
@import "../assets/styles/app-tokens";
@import "../assets/styles/ew-components";

.ew-page { @include app-tokens; }
.ew-page__body { padding: 1.75rem 2rem 2.5rem; }

.ew-toolbar { margin-bottom: 1rem; &.ew-toolbar--end { display: flex; justify-content: flex-end; } }
.ew-btn-primary { @include ew-btn-primary; }
.ew-btn-outline { @include ew-btn-outline; }
.ew-btn-outline--sm { height: 2rem; padding: 0 0.9rem; font-size: 0.78rem; }
.ew-actions-cell { display: flex; align-items: center; flex-wrap: wrap; gap: 0.75rem; }

// Interruptor estilo iOS para activar/desactivar — el checkbox real queda
// oculto pero accesible (foco/teclado/lector de pantalla siguen
// funcionando), solo se le da estilo al track+thumb de al lado.
.ew-toggle {
  display: inline-flex;
  align-items: center;
  cursor: pointer;

  input {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  }
}
.ew-toggle__track {
  position: relative;
  display: block;
  width: 2.6rem;
  height: 1.45rem;
  border-radius: 999px;
  background: var(--app-border);
  transition: background-color .15s ease;
}
.ew-toggle__thumb {
  position: absolute;
  top: 0.15rem;
  left: 0.15rem;
  width: 1.15rem;
  height: 1.15rem;
  border-radius: 50%;
  background: #fff;
  box-shadow: 0 1px 3px rgba(7, 50, 88, 0.3);
  transition: transform .15s ease;
}
.ew-toggle.is-on .ew-toggle__track { background: #1a7f4f; } // mismo verde que .ew-status.is-active
.ew-toggle.is-on .ew-toggle__thumb { transform: translateX(1.15rem); }
.ew-toggle input:focus-visible ~ .ew-toggle__track { outline: 3px solid var(--app-accent); outline-offset: 2px; }

.ew-icon-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  flex-shrink: 0;
  border-radius: 50%;
  border: 1px solid var(--app-border);
  background: #fff;
  color: var(--app-navy);
  cursor: pointer;

  svg { width: 1.05rem; height: 1.05rem; }

  &:hover { background: var(--app-surface-muted); }
  &:focus-visible { outline: 3px solid var(--app-accent); outline-offset: 2px; }
}

.ew-card { @include ew-card; }
.ew-table { @include ew-table; }
.ew-loading { @include ew-loading; }
.ew-loading-text { @include ew-loading-text; }
.ew-spinner { @include ew-spinner; }

.ew-filters {
  padding: 1.1rem 1.25rem;
  border-bottom: 1px solid var(--app-border);
}
.ew-filters__label {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  margin-bottom: 0.75rem;
  font-family: var(--app-font);
  font-size: 0.8rem;
  font-weight: 700;
  color: var(--app-muted);

  svg { width: 1rem; height: 1rem; }
}

.ew-search {
  position: relative;
  max-width: 22rem;
}
.ew-search__input { width: 100%; }

.ew-search__suggestions {
  position: absolute;
  z-index: 5;
  top: calc(100% + 0.35rem);
  left: 0;
  right: 0;
  margin: 0;
  padding: 0.35rem;
  list-style: none;
  background: #fff;
  border: 1px solid var(--app-border);
  border-radius: var(--app-radius-md);
  box-shadow: 0 10px 26px rgba(7, 50, 88, 0.12);
  max-height: 16rem;
  overflow-y: auto;
}
.ew-search__suggestion {
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
  padding: 0.5rem 0.6rem;
  border-radius: var(--app-radius-sm);
  cursor: pointer;

  &:hover { background: var(--app-surface-muted); }
}
.ew-search__suggestion-name {
  font-family: var(--app-font);
  font-size: 0.85rem;
  font-weight: 700;
  color: var(--app-navy);
}
.ew-search__suggestion-email {
  font-family: var(--app-font);
  font-size: 0.78rem;
  color: var(--app-muted);
}

.ew-pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  padding: 1rem 1.25rem;
  border-top: 1px solid var(--app-border);
}
.ew-pagination__btn { @include ew-pagination-btn; }

.ew-status {
  display: inline-flex; align-items: center; gap: 0.4rem;
  font-family: var(--app-font); font-size: 0.82rem; font-weight: 600; color: var(--app-muted);
  &::before { content: ''; width: 7px; height: 7px; border-radius: 999px; background: currentColor; }
  &.is-active { color: #1a7f4f; }
}

.ew-empty { @include ew-empty; padding: 3.5rem 1rem; }
.ew-empty__title { @include ew-empty-title; }
.ew-empty__text { @include ew-empty-text; }

.ew-form { display: flex; flex-direction: column; gap: 1rem; max-width: none; /* App.vue trae un .ew-form global con max-width:400px legado de los formularios de auth viejos; sin este reset se hereda igual (misma especificidad, propiedades distintas) */ }
.ew-form__field { display: flex; flex-direction: column; gap: 0.4rem; }
.ew-label { font-family: var(--app-font); font-size: 0.8rem; font-weight: 700; color: var(--app-navy); }
.ew-input { @include ew-input; }
.ew-input--block { width: 100%; }
.ew-modal__actions { display: flex; justify-content: flex-end; gap: 0.6rem; }

.ew-credit-target {
  margin: -0.25rem 0 0;
  font-family: var(--app-font);
  font-size: 0.88rem;
  color: var(--app-muted);

  strong { color: var(--app-navy); }
}

// Mismo patrón del campo de monto que NewPrepaid.vue (recarga de saldo del
// propio usuario), para que el abono manual desde este panel se sienta
// consistente con esa pantalla.
.ew-amount-field {
  display: flex;
  align-items: center;
  height: 3.25rem;
  padding: 0 1rem;
  border: 1px solid var(--app-border);
  border-radius: var(--app-radius-sm);
  background: #fff;
  gap: 0.5rem;

  &:focus-within { border-color: var(--app-accent); }

  &__prefix { font-family: var(--app-font); font-size: 1.15rem; font-weight: 700; color: var(--app-navy); }
  &__suffix { font-family: var(--app-font); font-size: 0.8rem; font-weight: 700; color: var(--app-muted); }

  &__input {
    flex: 1 1 auto;
    min-width: 0;
    border: none;
    outline: none;
    font-family: var(--app-mono);
    font-size: 1.4rem;
    font-weight: 700;
    color: var(--app-navy);
    background: transparent;

    &::placeholder { color: var(--app-border); font-weight: 400; }
    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }
    -moz-appearance: textfield;
  }
}

.ew-amount-preview {
  margin: -0.5rem 0 0;
  font-family: var(--app-font);
  font-size: 0.8rem;
  color: var(--app-muted);
}

.ew-quick-amounts { display: flex; flex-wrap: wrap; gap: 0.6rem; }
.ew-chip {
  height: 2.1rem;
  padding: 0 0.9rem;
  border-radius: 999px;
  border: 1px solid var(--app-border);
  background: #fff;
  font-family: var(--app-font);
  font-size: 0.8rem;
  font-weight: 700;
  color: var(--app-navy);

  &:hover { background: var(--app-surface-muted); }
  &:focus-visible { outline: 3px solid var(--app-accent); outline-offset: 2px; }
}
</style>

<style lang="scss">
@import "../assets/styles/app-tokens";
.ew-modal {
  .modal-content { border-radius: 0.85rem; border: none; }
  .modal-header { border-bottom: 1px solid $app-border; }
  .modal-title { font-family: $app-font-family; font-weight: 700; color: $app-navy; }
}
</style>



// WEBPACK FOOTER //
// src/components/Users.vue